import React, { useEffect, useState } from 'react';
import * as RiIcons from 'react-icons/ri';
import * as MdIcons from 'react-icons/md';
import * as FaIcons from 'react-icons/fa';
import { HiMiniAdjustmentsHorizontal } from 'react-icons/hi2';
import { ILayers } from 'contexts/@types/Layers';
import { InputSlider, Checkbox, Modal, Button } from 'components';
import { useMap } from 'hooks/useMap';
import { useLayers } from 'hooks/useLayers';
import { useTranslation } from 'react-i18next';
import * as Styled from './styles';
import iconeLoading from '../../assets/icone/icone_loading.gif';

import Notification from 'components/Notification';
import { CustomTooltip } from 'components/Tooltip';
import { mergeCategorias } from 'utils/mergeCategorias';
import { IStatus, verifyDownloadUrl } from 'services/downloadRequest';
import { parsePreference, SearchResultLayerPreference } from 'utils/mapPreferences';
import { captureMonitoringEvent } from 'services/monitoring';

interface ILayer {
  checked: boolean;
  layer: ILayers;
  layers?: ILayers[];
  layertitle: string;
  is_new: boolean;
  nm_category: string;
  setLayers?: React.Dispatch<React.SetStateAction<ILayers[]>>;
  handleChangeTabIndex: (index: number, text: string) => void;
}

export function Layer({ layer, layers, layertitle, setLayers, is_new, nm_category }: ILayer) {
  const LAYER_TITLE_MAX_LENGTH = 40;
  const { t: translation } = useTranslation();
  const [isActived, setIsActived] = useState<boolean>(false);
  const [opacity, setOpacity] = useState<number>(1);
  const [isLayerSampled, setIsLayerSampled] = useState<boolean>(true);

  const favoriteLayers = JSON.parse(localStorage.getItem('layersFavorite') || '[]')[nm_category] || [];
  const favorite = favoriteLayers.find((item: string) => item === layer.layertitle + (layer.layer_filter_id || '') + layer.layername);

  const [isLayerFavorite, setIsLayerFavorite] = useState<boolean>(!!favorite);

  const {
    handleAddOverlayLayer,
    handleRemoveOverlayLayer,
    handleChangeOpacityLayer,
    setOpenLayerFeaturesModal,
    setOpenLayerDetailsModal,
    layerActiveFeature,
    handleChangeVisibility
  } = useMap();

  const [downloadFinished, setDownloadFinished] = useState<string>('');
  const [downloadError, setDownloadError] = useState<string>('');
  const [newDownload, setNewDownload] = useState<string>('');
  const [outsideSource, setOutsideSource] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [validUrl, setValidUrl] = useState<boolean>(false);

  useEffect(() => {
    if (outsideSource) {
      checkOutsideSource(layer.metadados.fonte);
    }
  }, [outsideSource])

  const identifier = `${layer.id}_${layer.layer_filter_id}`;

  const {
    handleAddLayer,
    handleRemoveLayer,
    handleChangeLayerOptionsOpen,
    layersActived,
    downloadNotifOpen,
    setDownloadNotifOpen,
    setCategoryList,
  } = useLayers();

  const handleFavoriteLayer = () => {
    const layersStoraged = JSON.parse(localStorage.getItem('layersFavorite') || '[]');
    const array = layersStoraged[nm_category] || [];

    if (!isLayerFavorite) {
      localStorage.setItem(
        'layersFavorite',
        JSON.stringify({ ...layersStoraged, [nm_category]: [...array, layer.layertitle + (layer.layer_filter_id || '') + layer.layername] }),
      );

      return setIsLayerFavorite(true);
    }

    if (array.length === 1) {
      delete layersStoraged[nm_category];
      localStorage.setItem('layersFavorite', JSON.stringify(layersStoraged));
    } else {
      const layerstoStoraged = array.filter((item: string) => item !== layer.layertitle + (layer.layer_filter_id || '') + layer.layername);
      localStorage.setItem('layersFavorite', JSON.stringify({ ...layersStoraged, [nm_category]: layerstoStoraged }));
    }

    if (layers && setLayers) {
      const newLayers = layers.filter(item => item.layertitle !== layertitle);
      setLayers(newLayers);
    }

    return setIsLayerFavorite(false);
  };

  const handleChangeOptionOpen = (layer: ILayers, option: string) => {
    handleChangeLayerOptionsOpen(layer, option);
  };

  useEffect(() => {
    if (isLayerSampled) {
       handleChangeVisibility(layer.layertitle + (layer.layer_filter_id || '') + layer.layername, true);
    } else {
       handleChangeVisibility(layer.layertitle + (layer.layer_filter_id || '') + layer.layername, false);
    }
  }, [isLayerSampled]);

  useEffect(() => {
    const layerActived = layersActived.find(
      (layerActived: ILayers) => layerActived.id === layer.id && (layerActived.layer_filter_id === layer.layer_filter_id),
    );
    if (layerActived) {
      setIsActived(true);
    } else {
      setIsActived(false);
    }
  }, [layersActived]);

  function disableLayer() {
    handleRemoveLayer(layer);
    handleRemoveOverlayLayer(layer.layertitle + (layer.layer_filter_id || '') + layer.layername);
    const uuidLayer = layer.layertitle + (layer.layer_filter_id || '') + layer.layername;

    if (uuidLayer === layerActiveFeature) {
      setOpenLayerFeaturesModal(false);
      setOpenLayerDetailsModal(false);
    }
  }

  function removeLayer() {
    disableLayer()

    const categoriasStr = sessionStorage.getItem('pesquisas');
    let categorias = categoriasStr ? JSON.parse(categoriasStr) : [];

    const categoria = categorias.find((cat: any) => cat.id_categoria === 1000);
    const tema = categoria.temas.find((t: any) => t.id_tema === 1000);

    tema.layers = tema.layers.filter((l: any) => l.layer_filter_id !== layer.layer_filter_id);

    const savedSearchResults = parsePreference<SearchResultLayerPreference[]>('searchResultLayers', []);
    localStorage.setItem(
      'searchResultLayers',
      JSON.stringify(savedSearchResults.filter(item => item.layer_filter_id !== layer.layer_filter_id)),
    );

    if (tema.layers.length === 0) {
      categorias = categorias.filter((cat: any) => cat.id_categoria !== 1000);
    }
    setTimeout(() => {
      sessionStorage.setItem('pesquisas', JSON.stringify(categorias));
      setCategoryList(prev => mergeCategorias(prev, categorias));
    }, 10)
  }

  async function downloadLayer() {
    if (!!layer.metadados && !layer.metadados.dado_primario) return setOutsideSource(identifier);

    if (downloadNotifOpen) {
      setNewDownload(identifier);
      return setTimeout(() => {
        setNewDownload('');
      }, 3000);
    }

    setDownloadNotifOpen(`${layer.id}_${layer.layer_filter_id}`);

    const xhr = new XMLHttpRequest();

    xhr.open('GET', layer.link_shapefile, true);
    xhr.responseType = 'blob';

    xhr.onload = function () {
      if (xhr.status === 200) {
        void captureMonitoringEvent({
          tipo_evento: 'download',
          identificador_camada: layer.layername || String(layer.id),
        });
        const blob = new Blob([xhr.response], { type: 'application/octet-stream' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${layer.layertitle}.zip`;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      }
      setNewDownload('');
      setDownloadNotifOpen('');
      setDownloadFinished(identifier);

      setTimeout(() => {
        setDownloadFinished('');
      }, 5000);
    };

    xhr.onerror = function () {
      setNewDownload('');
      setDownloadNotifOpen('');
      setDownloadFinished('');
      setDownloadError(identifier);

      setTimeout(() => {
        setDownloadError('');
      }, 5000);
    };

    xhr.send();
  }

  function viewLayer(): void{
    const url = new URL(layer.host_url);

    const width = (window.innerWidth - 18).toString();
    const height = (window.innerHeight - 340).toString();

    url.search = new URLSearchParams({
      service: 'WMS',
      version:'1.1.0',
      request:'GetMap',
      layers:layer.layername,
      bbox:'-65.11997985839844,-31.5839900970459,-34.83262634277344,-0.790183186531067',
      width: width,
      height: height,
      srs:'EPSG:4674',
      styles:'',
      format:'application/openlayers',
      CQL_FILTER: layer.layer_query_filter ?? '',
    }).toString();
    void captureMonitoringEvent({
      tipo_evento: 'geoserver',
      identificador_camada: layer.layername || String(layer.id),
    });
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  }

  const handleOutsideSource = () => {
    void captureMonitoringEvent({
      tipo_evento: 'download',
      identificador_camada: layer.layername || String(layer.id),
    });
    window.open(layer.metadados.fonte, '_blank', 'noopener,noreferrer');
    setOutsideSource('');
  };

  const checkOutsideSource = async (url: string) => {
    setLoading(true);

    const resp = await verifyDownloadUrl<IStatus>(url);
    
    setValidUrl(resp.ok);
    setLoading(false);
  }

  const getModalData = () => {
    if (loading) {
      return (
        <>
          <p>
            <strong>
              {layer.metadados.organizacao ? (
                <>
                  {`Este dado é produzido pelo(a) `} <span className="notranslate">{layer.metadados.organizacao}</span>.
                </>
              ) : (
                'Este dado é produzido por uma fonte externa.'
              )}
            </strong>
          </p>
          <p>
            <strong>Verificando a disponibilidade do link de download. Aguarde um momento...</strong>
          </p>
        </>
      )
    }
    else if (!validUrl) {
      return (
        <>
          <p>
            <strong>
              O link para download da camada não está disponível no momento. <br/>
              Para corrigir, entre em contato com o administrador do sistema.
            </strong>
          </p>
        </>
      );
    }

    return (
      <>
        <p>
          <strong>
            {layer.metadados.organizacao ? (
              <>
                {`Este dado é produzido pelo(a) `} <span className="notranslate">{layer.metadados.organizacao}</span>.
              </>
            ) : (
              'Este dado é produzido por uma fonte externa.'
            )}
          </strong>
        </p>
        <p>
          <strong>Deseja ir para a página deste produtor?</strong>
        </p>
      </>
    )
  }

  function truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  }

  return (
    <Styled.Wrapper isActived={isActived}>
      <Styled.Content>
        <div className="layer">
          <Checkbox
            checked={isActived}
            onChecked={value => {
              if (value) {
                handleAddLayer(layer);
                handleAddOverlayLayer(layer);
              } else {
                disableLayer();
              }
            }}
          />

          <CustomTooltip title={layertitle} placement="top" disableInteractive>
            <Styled.LayerTitle>{truncateText(layertitle, LAYER_TITLE_MAX_LENGTH)}</Styled.LayerTitle>
          </CustomTooltip>

          {/* TODO: Tradução */}
          {is_new && <span>novo</span>}
        </div>

        {isActived && (
          <Styled.OptionsBlock>
            <div className="menu-icons">
              <Styled.Button
                label={translation('mapPage.sideBar.layers.legend')}
                onClick={() => handleChangeOptionOpen(layer, 'subtitle')}
              >
                <RiIcons.RiMenu2Fill size={20} color="#000" />
              </Styled.Button>

              <Styled.Button label={translation('mapPage.sideBar.layers.favorite')} onClick={handleFavoriteLayer}>
                <FaIcons.FaStar size={20} color={isLayerFavorite ? '#FFD700' : '#000'} />
              </Styled.Button>

              <Styled.Button
                label={translation('mapPage.sideBar.layers.showHide')}
                onClick={() => setIsLayerSampled(!isLayerSampled)}
              >
                {!isLayerSampled ? (
                  <RiIcons.RiEyeOffFill size={20} color="#000" />
                ) : (
                  <RiIcons.RiEyeFill size={20} color="#000" />
                )}
              </Styled.Button>

              <Styled.Button label={translation('mapPage.sideBar.layers.filter')} disabled>
                <HiMiniAdjustmentsHorizontal size={20} color="#808080" />
              </Styled.Button>

              <Styled.Button
                label={translation('mapPage.sideBar.layers.information')}
                onClick={() => handleChangeOptionOpen(layer, 'information')}
              >
                <RiIcons.RiInformationLine size={20} color="#000" />
              </Styled.Button>

              <Styled.Button label={'Geoserver'} onClick={viewLayer}>
                <MdIcons.MdMap size={20} color={'#000'} />
              </Styled.Button>
              
              <Styled.Button label={translation('mapPage.sideBar.layers.download')} onClick={downloadLayer}>
                <Styled.LinkDownloadLayer>
                  <MdIcons.MdFileDownload size={20} color="#000" />
                </Styled.LinkDownloadLayer>
              </Styled.Button>
              { nm_category === "Pesquisas Recentes" && 
                <Styled.Button
                  label={"Excluir"}
                  onClick={removeLayer}
                >
                  <RiIcons.RiDeleteBin7Line size={20} color="#000" />
                </Styled.Button>
              }
            </div>
          </Styled.OptionsBlock>
        )}
      </Styled.Content>

      {outsideSource && (
        <Modal onDisabled={() => setOutsideSource('')}>
          <Styled.Modal>
            {
              getModalData()
            }

            {
              <div className="buttons">
                {
                  loading?
                  <img style={{width: '50px'}} src={iconeLoading} alt="Loading" />
                  :
                  <>
                    <Button outline={true} onClick={() => setOutsideSource('')}>
                      {validUrl? 'Cancelar' : 'Ok'}
                    </Button>

                    {validUrl && <Button onClick={handleOutsideSource}> Confirmar </Button>}
                  </>
                }
              </div>
            }
          </Styled.Modal>
        </Modal>
      )}

      {isActived && (
        <Styled.Content>
          {/* `${translation('mapPage.sideBar.layers.transparency')}:` */}
          <InputSlider
            label={''}
            min={0}
            max={1}
            step={0.01}
            value={opacity}
            onChange={({ target }) => {
              handleChangeOpacityLayer(layer.layertitle + (layer.layer_filter_id || '') + layer.layername, Number(target.value));
              setOpacity(Number(target.value));
            }}
          />
        </Styled.Content>
      )}
      {downloadNotifOpen === identifier && (
        <Notification
          text={`Preparando o download da camada ${layer.layertitle}`}
          icon={{
            component: FaIcons.FaCircleNotch,
            color: '#69a7e0',
            spinner: true,
          }}
        />
      )}

      {downloadFinished === identifier && (
        <Notification
          text={'Download realizado com sucesso.'}
          icon={{
            component: FaIcons.FaCheckCircle,
            color: '#22CB72',
          }}
        />
      )}

      {downloadError === identifier && (
        <Notification
          text={'Erro ao realizar o download.'}
          icon={{
            component: FaIcons.FaTimesCircle,
            color: '#D3202C',
          }}
        />
      )}

      {newDownload === identifier && (
        <Notification
          text={`Existe um download em andamento. Por favor, aguarde!`}
          icon={{
            component: FaIcons.FaTimesCircle,
            color: '#D3202C',
          }}
          bottom="160px"
        />
      )}
    </Styled.Wrapper>
  );
}
